
<script setup>
/*

	Questions:
		What fields are required?
		How to handle TIN/NPI/CMS?
			- One field with option to select which one is being added?

 */

import { computed, onBeforeMount, onMounted, ref } from 'vue'
import InputText from '@/components/Forms/InputText.vue'
import Combobox from '@/components/Forms/Combobox.vue'
import ValidationError from '@/components/Forms/InputError.vue'
import { organizationsApi } from '@/api/Organizations/OrganizationsApi.js'
import { ApiGatewayResponse, HttpStatusCode } from '@/api/ApiGateway.js'
import { OrganizationModalStep } from '@/models/enums/OrganizationModalStep.js'
import { OrganizationModalData } from '@/models/enums/OrganizationModalData.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { useCreateOrganizationStore } from '@/stores/organizationStore.js'
import { FormData } from '@/models/enums/FormData.js'
import BaseModal from '@/components/Alerts/BaseModal.vue'
import { ButtonSize, ButtonType, ButtonWidth } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import { Switch } from '@headlessui/vue'

const orgCreateStore = useCreateOrganizationStore()

const isLoading = ref(false)
const error = ref(null)

const organization_name = ref('')
const organization_name_dba = ref('')
const organization_address = ref('')
const city = ref('')
const states = FormData.STATES
const selected_state = ref('')
const zip = ref('')
const ccn = ref('')
const facility_ids = ref('')
const search_results = ref(null)
const use_dba = ref(true)

const selected_organization = ref(null)
const orgNotFound = ref(false)

const facilityIdsEditModalConfig = computed(() => {
	return {
		title: 'Facility IDs',
		message: 'Enter any number of Facility IDs, each separated by a comma.',
		text: facility_ids.value,
		buttonSize: ButtonSize.Small,
		buttonWidth: ButtonWidth.Full,
		buttonText: 'Save',
		placeholder: '0123456789, 0987654321',
		onButtonClicked: (text) => { saveFacilityIds(text) },
		inputFilter: filterInput,
	}
})

const filterInput = (event) => {
	event.target.value = event.target.value.replace(/[^a-zA-Z0-9,]/g, '')
}

defineProps({
	show: { type: Boolean, default: false },
})

onBeforeMount(()=>{
	//Allow users to clear the State selection in adv search
	if (states[0] !== "") states.unshift("")
})

onMounted(()=>{
	clearFields();
})

/**
 * Check if the entered data matches any existing records
 */
function findOrganization()
{
	error.value = null
	isLoading.value = true

	if (validateFields())
	{
		//if CCN is set, only it gets searched on. And since it's currently required, it means the other fields are ignored
		//	We should probably do a CCN or the other fields
		const searchCriteria = {
			'ccn': ccn.value,
			'organization_name': organization_name.value,
			'organization_name_dba': organization_name_dba.value,
			'address': organization_address.value,
			'city': city.value,
			'state': selected_state.value,
			'zip': zip.value,
		}

		organizationsApi.search(
			searchCriteria,
			'organization_name',
			5,
			[],
			(response) =>
			{
				isLoading.value = false
				orgNotFound.value = response.length === 0

				if (response.length > 0)
				{
					search_results.value = response
					selected_organization.value = search_results.value[0]
					orgCreateStore.setCurrentStep(OrganizationModalStep.SEARCH_RESULTS)
				}
				else if (error.value === null)
				{
					createOrganization()
				}
			}, (e) =>
			{
				error.value = e.showErrorBanner()

				isLoading.value = false
			})
	}
	else
	{
		isLoading.value = false
	}

}

/**
 * Checks that required fields have been entered then initiates request to create organization
 */
function createOrganization()
{
	if (validateFields())
	{
		const org_data = {
			ccn: ccn.value,
			organization_name: organization_name.value,
			organization_name_dba: organization_name_dba.value,
			street: organization_address.value,
			city: city.value,
			state: selected_state.value,
			zip: zip.value,
			use_dba: use_dba.value,
			facility_ids: facility_ids.value,
		}

		organizationsApi.create(org_data, onCreated, onCreatedError)
	}
}

/**
 * Called when organization successfully created.
 */
function onCreated()
{
	orgCreateStore.showSuccessAlert()
}

/**
 * Called when there was an error creating the organization
 * @param p_err
 */
function onCreatedError(p_err)
{
	if (p_err.error.status === 403)
	{
		error.value = p_err.showErrorBanner()
		orgCreateStore.toggleCreateModal(false)
	}
	else if (p_err.error.status !== 500)
	{
		error.value = p_err.showErrorBanner()
		orgCreateStore.toggleCreateModal(true)//Show fields so they can see the required messages
	}
	else
	{
		console.log("onCreatedError", p_err);
		//orgCreateStore.showFailedAlert()
		p_err.showErrorBanner("Failed to create organization: " + p_err.errorMessages())
	}
	//
}

/**
 * Creates a local errors object to flag missing fields rather than relying on the server side code to catch it.
 * Fills ref 'error' variable
 * @param response
 * @returns {boolean} True if required fields entered
 */
function validateFields(response)
{
	const errors = []

	//Fields that are required
	const entryFields = new Map()
	entryFields.set('Name', ['organization_name', organization_name.value])
	entryFields.set('DBA Name', ['organization_name_dba', organization_name_dba.value])
	entryFields.set('Address', ['street', organization_address.value])
	entryFields.set('City', ['city', city.value])
	entryFields.set('State', ['state', selected_state.value])
	entryFields.set('ZIP code', ['zip', zip.value])

	entryFields.forEach((value, key) =>
	{
		if (!value[1])
		{
			errors.push(
				{
					'title': 'Invalid Attribute',
					'status': 422,
					'detail': `${key} is required.`,
					'source': `organization.${value[0]}`
				}
			)
		}
	})

	if (errors.length > 0)
	{
		error.value = new ApiGatewayResponse({
			'status': HttpStatusCode.ValidationError,
			'response': {
				'data': {
					'errors': errors
				}
			}
		}, response)
		return false
	}

	return true
}

/**
 * Clears all input fields
 */
function clearFields()
{
	organization_name.value = ''
	organization_name_dba.value = ''
	ccn.value = ''
	organization_address.value = ''
	city.value = ''
	selected_state.value = ''
	zip.value = ''
	error.value = null
	use_dba.value = true
	facility_ids.value = ''
}

/**
 * Clears the fields and hides the modal
 */
function dismissModal()
{
	clearFields()
	orgCreateStore.toggleCreateModal(false)
}

const searchResultsTitle = computed(() => {
	let label = ''
	if (search_results.value.length === 1)
	{
		label = "Similar Organization Found"
	}
	else if (search_results.value.length > 1)
	{
		label = "Similar Organizations Found"
	}
	return label
})

function getOrgDisplayName(p_org_name, p_org_name_dba)
{
	if (p_org_name_dba)
	{
		return `${p_org_name}  (${p_org_name_dba})`
	}
	return p_org_name
}

function saveFacilityIds(text) {
	// Trim leading and trailing commas, then make the string prettier
	facility_ids.value = text.replace(/^,+|,+$/g, '').replace(/,+/g, ', ')
}

</script>

<template>
	<BaseModal :show="show" border-classes="border-t-6 border-tertiary">
		<div class="w-[692px]">
			<div v-if="orgCreateStore.getCurrentStep === OrganizationModalStep.CREATE">
				<div class="flex justify-between border-b border-neutral w-full pb-6">
					<p class="flex-grow text-xl font-bold text-heading">Create Organization</p>
					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal(false)">
				</div>

				<form>
					<button type="submit" disabled style="display: none" aria-hidden="true"></button>

					<div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 w-full pt-6">
						<!-- Legal name / DBA name -->
						<InputText label="Legal name*" type="text" placeholder="Legal name" input-height="h-10" autocomplete="off"
						           v-model="organization_name" :errors="error" error-field="organization.organization_name" icon="edit" />
						<div class="relative">
							<Popper class="absolute right-0" :content="use_dba ? OrganizationModalData.USE_DBA_ON_TOOLTIP : OrganizationModalData.USE_DBA_OFF_TOOLTIP" hover arrow placement="bottom-start">
								<div class="flex space-x-2 items-center">
									<span class="text-label cursor-default">Use DBA</span>
									<Switch
										v-model="use_dba"
										:class="use_dba ? 'bg-primary' : 'bg-primary-darker'"
										class="inline-flex h-4 w-9 m-1 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75"
									>
										<span class="sr-only">Use DBA</span>
										<span
											aria-hidden="true"
											:class="use_dba ? 'translate-x-4 border border-primary' : 'translate-x-0 border border-primary-darker'"
											class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out"
										/>
									</Switch>
								</div>
							</Popper>
							<InputText label="DBA name*" type="text" placeholder="DBA name" input-height="h-10" autocomplete="off"
							           v-model="organization_name_dba" :errors="error" error-field="organization.organization_name_dba" icon="edit" />
						</div>

						<!-- Address / City -->
						<InputText label="Address*" type="text" placeholder="Enter physical address" input-height="h-10"
						           v-model="organization_address" :errors="error" error-field="organization.street" />

						<InputText label="City*" type="text" placeholder="Enter city" v-model="city"
						           input-height="h-10" :errors="error" error-field="organization.city" />

						<!-- State / ZIP -->
						<Combobox class="flex-1" id="combo_add_org_state" v-model="selected_state" :items="states" title="State*" placeholder="Select state"
						          :show-options-on-top="true" input-height="h-10" :errors="error" error-field="organization.state" :readonly="false" />

						<InputText class="flex-1" label="Zip code*" type="number" placeholder="Enter 5 digit code" :max-length=5 input-height="h-10"
						           v-model="zip" :errors="error" error-field="organization.zip" />

						<!-- CCN / Facility IDs -->
						<InputText label="CCN" type="text" placeholder="Enter CCN" v-model="ccn"
										class="flex-1" input-height="h-10" :errors="error" error-field="organization.ccn" :tooltip="OrganizationModalData.CCN_TOOLTIP"
										tooltip-location="bottom" />

						<InputText class="flex-1" label="Facility IDs" type="text" placeholder="Enter Facility IDs" v-model="facility_ids" autocomplete="off" input-height="h-10"
											 :errors="error" error-field="organization.facility_ids" :use-edit-modal="true" :edit-modal-config="facilityIdsEditModalConfig" />
					</div>

					<div class="border-b border-neutral w-full h-[1px] mt-1 mb-5"></div>

					<!-- Save/Cancel -->
					<div class="flex w-full flex-col-reverse sm:flex-row">
						<div class="sm:mr-auto mt-1 sm:mt-0"></div>
						<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="dismissModal(false)"
						              class="mt-3 sm:mt-0 sm:mr-4" :disabled="isLoading" />
						<SubmitButton label="Create Organization" :size="ButtonSize.Small" :loading="isLoading" @click.prevent="findOrganization" />

					</div>

					<ValidationError :errors="error" :field="null" />
				</form>
			</div>

			<div v-if="orgCreateStore.getCurrentStep === OrganizationModalStep.SEARCH_RESULTS">
				<div class="flex items-center w-90">
					<p class="flex-grow text-xl font-bold text-heading leading-7">{{ searchResultsTitle }}</p>
					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal">
				</div>

				<div class="mt-2">
					<p class="text-heading font-bold">Your Organization:</p>
					<div class="ml-3 text-sm/6">
						<p class="text-label">{{ getOrgDisplayName(organization_name, organization_name_dba) }}</p>
						<p v-show="ccn.length > 0" class="text-label">CCN: {{ ccn }}</p>
						<p class="text-label">{{ organization_address }}</p>
						<p class="text-label">{{ `${city}, ${selected_state} ${zip}` }}</p>
					</div>
				</div>

				<!-- It's unlikely this will be shown with so much required data, but just in case -->
				<p class="text-heading font-bold mt-8">Similar Organization(s):</p>
				<fieldset class="max-h-70 overflow-y-scroll">
					<div v-for="(result, index) in search_results" :key="result.id" class="relative flex items-start cursor-pointer pb-2 pt-2"
							:class="{ 'border-b border-neutral' : index !== search_results.length-1 }"
							@click="selected_organization = result">
						<div class="ml-3 text-sm/6">
							<p class="text-label">{{ getOrgDisplayName(result.organization_name, result.organization_name_dba) }}</p>
							<p class="text-label">CCN: {{ result.ccn }}</p>
							<p :id="`${result.id}-name`" class="text-label">{{ result.street }}</p>
							<p :id="`${result.id}-address`" class="text-label">{{ `${result.city}, ${result.state} ${result.zip}` }}</p>
							<p v-if="result.facility_type != null" :id="`${result.id}-specialty`"
								class="font-semibold text-label">{{ `Specialty: ${result.facility_type}` }}</p>
						</div>
					</div>
				</fieldset>

				<div class="flex flex-col sm:flex-row gap-x-4 items-center">
					<SubmitButton label="Cancel" class="w-full mt-6" :loading="isLoading" type="outline" @click.prevent="orgCreateStore.toggleCreateModal(true)" />
					<SubmitButton class="w-full mt-6" label="Create New" :loading="isLoading" @click="createOrganization" />
				</div>

				<ValidationError :errors="error" :field="null" />
			</div>

			<div v-if="orgCreateStore.getCurrentStep === OrganizationModalStep.CREATED" class="flex flex-col items-center">
				<p class="flex-grow text-xl font-bold text-heading leading-7">Organization Created Successfully</p>
				<div class="w-[90px] h-[3px] bg-primary rounded-[2px] my-6" />
				<p class="text-grayscale-body text-center font-medium leading-6">Your Organization has been created.</p>
				<SubmitButton class="w-48 mt-6" label="OK" :loading="isLoading" @click="dismissModal" />
			</div>

			<div v-if="orgCreateStore.getCurrentStep === OrganizationModalStep.FAILED" class="flex flex-col items-center w-100">
				<p class="flex-grow text-xl font-bold text-heading leading-7">Organization Creation Failed</p>
				<div class="w-[90px] h-[3px] bg-primary rounded-[2px] my-6" />
				<p class="text-grayscale-body text-center font-medium leading-6">Failed to create organization.</p>
				<SubmitButton class="w-48 mt-6" label="OK" :loading="isLoading" @click="orgCreateStore.toggleCreateModal(true)" />
			</div>
		</div>
	</BaseModal>
</template>

<style scoped>


</style>